import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";

import GeneralPageContext from "../../templates/GeneralPageContext";

import { StandardLayout as Layout } from "../../layouts/StandardLayout";

import { isDebug } from "../../lib/helper/variables";
import withSeo from "../../hoc/withSeo/withSeo";
import { CookieConsent } from "../CookieConsent";
import { handleURLParams } from "./handleURLParams";

const layoutSettingDefaults = {
    formId: null,
    showNavigation: true,
    showMainNavigation: true,
    showCountryBanner: true,
    showBreadcrumb: true,
    slimTopBar: false,
    showFooter: true,
    showFooterNavigation: true,
    showAnchorNavigation: false,
    reducedContentWidth: false
};

function WebPage({
    anchorNavData = [],
    anchorNavCameraData,
    localeVariants = [],
    data,
    Hero,
    Content,
    contactPerson,
    contactButton,
    entryPage,
    pageContext,
    photoCredit,
    location,
    breadcrumb,
    finderApplications = [],
    finderCategories = [],
    children,
    formData = {},
    additionalContext,
    cookieGroups,
    cameras,
}) {
    if (isDebug) {
        console.log("The following data was loaded");
        console.log("Context Data", pageContext);
        console.log("CMS Data", data);
    }

    const [cookieConsentReopend, toggleCookieConsent] = useState(false);

    const layout = useMemo(() => {
        const layoutSettings =
            typeof pageContext.layoutSettings === "string"
                ? JSON.parse(pageContext.layoutSettings)
                : pageContext.layoutSettings;

        return {
            ...layoutSettingDefaults,
            ...layoutSettings
        };
    }, [pageContext.layoutSettings, layoutSettingDefaults]);

    const contextValues = useMemo(
        () => ({
            id: pageContext.id,
            type: pageContext.pageType,
            currentSlug: location.pathname.replace(/\/[a-zA-Z]\/\d+\/.*$/, "/"),
            locale: pageContext.locale,
            localeVariants,
            contactPerson,
            contactButton,
            entryPage,
            department: data.config.department,
            // Override the default settings with layout settings from DatoCMS
            layout,
            stdPages: data.stdPages,
            domains: data.domains.nodes,
            currency: data.config.currency,
            // hideAnchorNav,
            finderApplications,
            finderCategories,
            toggleCookieConsent,
            cameraTeaserWithCompare: false,
            cameras: layout.formId === 'registration-form' ? cameras : [],
            ...additionalContext
        }),
        [pageContext, data, additionalContext, layoutSettingDefaults, layout]
    );

    useEffect(() => {
        handleURLParams(data.campaigns.nodes);
    }, [data.campaigns.nodes]);

    return (
        <GeneralPageContext.Provider value={contextValues}>
            <Layout
                layoutSettings={layout}
                Hero={Hero}
                Content={Content}
                anchorNavData={anchorNavData}
                anchorNavCameraData={anchorNavCameraData}
                mainNavData={data.navigation}
                satelliteNavData={data.satelliteNavigation || {}}
                breadcrumb={breadcrumb || []}
                config={data.config || {}}
                footerData={{
                    ...(data.footerData || {}),
                    photoCredit
                }}
                locationState={location.state}
                formData={formData}
            >
                {children}
                <CookieConsent
                    groups={cookieGroups}
                    reopened={cookieConsentReopend}
                    setReopened={toggleCookieConsent}
                />
            </Layout>
        </GeneralPageContext.Provider>
    );
}

WebPage.defaultProps = {
    anchorNavData: [],
    localeVariants: []
};

WebPage.propTypes = {
    pageContext: PropTypes.object.isRequired,
    data: PropTypes.shape({
        config: PropTypes.object.isRequired,
        navigation: PropTypes.object.isRequired,
        footerData: PropTypes.object.isRequired
    }).isRequired,
    photoCredit: PropTypes.arrayOf(
        PropTypes.shape({
            copyright: PropTypes.string.isRequired
        })
    ),
    location: PropTypes.any.isRequired,
    anchorNavData: PropTypes.array,
    localeVariants: PropTypes.arrayOf(
        PropTypes.shape({
            locale: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired
        })
    )
};

const WebPageWithSeo = withSeo(WebPage);

export { WebPage, WebPageWithSeo };
